import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Category, Customer, MealPeriod, MealPlanSale, MealPlanSubscription, MealPlanType, MealPlanWeek, MenuItem, Order, WeeklyBooking } from '../../types';
import MealPlansTab from '../loyalty/MealPlansTab';
import MealPlanNewCustomerForm from '../loyalty/MealPlanNewCustomerForm';
import { autoBookSubscriptionForWeek, mealPlanSubscriptionsService, mealPlanTypesService, mealPlanWeeksService, ordersService, weeklyBookingsService } from '../../lib/database';
import { cancelMealPlanLocally, mealPlanSalePriceDollars, subscriptionCoversWeek } from '../../lib/mealPlanSale';
import { applyMealPlanPickupToggles } from '../../lib/mealPlanRedeem';
import { dayKeysForWeek, isBeforeCancellationCutoff, nextWeekId, planFitsWeek, weekIdContaining } from '../../lib/mealPlanWeek';

interface PosMealPlanOverlayProps {
  onClose: () => void;
  customers: Customer[];
  menuItems: MenuItem[];
  categories: Category[];
  orders: Order[];
  mealPlansEnabled: boolean;
  currentStaffId?: number | string;
  currentStaffName?: string;
  isManager?: boolean;
  onSell: (customer: Customer, plans: MealPlanType[], weekId: string) => Promise<void>;
  /** Open the normal POS refund screen for this sale. The plan is cancelled after that refund is recorded. */
  onOpenSaleRefund: (order: Order, subscription: MealPlanSubscription) => void;
  /** Re-open payment for a meal-plan ticket that was left unpaid. */
  onResumePayment: (order: Order) => void;
  /** Open order details (receipt / resend email) for a paid till sale. */
  onOpenSaleDetails: (order: Order) => void;
  refreshToken?: number;
}

const DAY = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatSellWeek(weekId: string): string {
  const days = dayKeysForWeek(weekId);
  const label = (dayKey: string, index: number) => {
    const [, month, date] = dayKey.split('-');
    return `${DAY[index]} ${Number(date)} ${MONTH[Number(month) - 1] || ''}`.trim();
  };
  return `${label(days[0], 0)} – ${label(days[6], 6)}`;
}

function weekPillDates(weekId: string): string {
  const days = dayKeysForWeek(weekId);
  const [, startMonth, startDate] = (days[0] || '').split('-');
  const [, endMonth, endDate] = (days[6] || '').split('-');
  const startLabel = MONTH[Number(startMonth) - 1] || '';
  const endLabel = MONTH[Number(endMonth) - 1] || '';
  if (!startLabel || !endLabel) return formatSellWeek(weekId);
  if (startMonth === endMonth) return `${startLabel} ${Number(startDate)}–${Number(endDate)}`;
  return `${startLabel} ${Number(startDate)} – ${endLabel} ${Number(endDate)}`;
}

function bookingDayLabel(dayKey: string): string {
  const [year, month, date] = dayKey.split('-').map(Number);
  if (!year || !month || !date) return dayKey;
  const weekday = new Date(Date.UTC(year, month - 1, date)).getUTCDay();
  const index = weekday === 0 ? 6 : weekday - 1;
  return `${DAY[index] || ''} ${date} ${MONTH[month - 1] || ''}`.trim();
}

function formatRecordedAt(ms?: number): string {
  if (!ms) return 'time not recorded';
  return new Intl.DateTimeFormat('en-NZ', {
    timeZone: 'Pacific/Auckland',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  }).format(ms);
}

function weekRole(weekId: string): string {
  const current = weekIdContaining();
  const upcoming = nextWeekId();
  if (weekId === current) return 'This week';
  if (weekId === upcoming) return 'Next week';
  if (weekId < current) return 'Past week';
  return 'Later week';
}

function planIncludes(plan: MealPlanType): string {
  const meals = plan.periods.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(', ');
  const days = plan.daysOfWeek.map((n) => DAY[n - 1] || '').filter(Boolean).join(' ');
  return [meals, days].filter(Boolean).join(' · ');
}

function paymentSourceLabel(source?: string): string {
  if (source === 'pos') return 'Paid on the till';
  if (source === 'online') return 'Paid on phone or website';
  if (source === 'complimentary') return 'No payment';
  return 'Payment not recorded';
}

const PERIODS: MealPeriod[] = ['breakfast', 'lunch', 'dinner'];

function MealPickupModal({
  customerName,
  weeks,
  bookingsLoaded,
  busy,
  onClose,
  onBook,
  onSave,
}: {
  customerName: string;
  weeks: { sub: MealPlanSubscription; meals: WeeklyBooking[] }[];
  bookingsLoaded: boolean;
  busy: boolean;
  onClose: () => void;
  onBook: (sub: MealPlanSubscription) => void;
  onSave: (pickUpIds: string[], undoIds: string[]) => void;
}) {
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const weeksRef = useRef(weeks);
  weeksRef.current = weeks;
  const mealKey = weeks
    .map((week) => week.meals.map((meal) => `${meal.id}:${meal.status}`).join(','))
    .join('|');

  useEffect(() => {
    const next: Record<string, boolean> = {};
    for (const week of weeksRef.current) {
      for (const meal of week.meals) {
        if (meal.status === 'cancelled') continue;
        next[meal.id] = meal.status === 'redeemed';
      }
    }
    setPicked(next);
  }, [mealKey]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const changes = weeks.flatMap((week) =>
    week.meals.filter((meal) => {
      if (meal.status === 'cancelled') return false;
      return Boolean(picked[meal.id]) !== (meal.status === 'redeemed');
    }),
  );

  const toggle = (mealId: string) => {
    setPicked((prev) => ({ ...prev, [mealId]: !prev[mealId] }));
  };

  const markWeek = (meals: WeeklyBooking[], on: boolean) => {
    setPicked((prev) => {
      const next = { ...prev };
      for (const meal of meals) {
        if (meal.status !== 'cancelled') next[meal.id] = on;
      }
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="meal-pickup-title"
        className="flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-gray-200 px-5 py-4">
          <div>
            <h3 id="meal-pickup-title" className="text-lg font-bold text-gray-900">Pickups · {customerName}</h3>
            <p className="text-sm text-gray-500">Each week is listed below. Switch a meal on when it is picked up, then save once.</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-100">
            Close
          </button>
        </div>
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-4">
          {!bookingsLoaded && <p className="text-sm text-gray-500">Checking meals…</p>}
          {weeks.map(({ sub, meals }) => {
            const days = [...new Set(meals.map((meal) => meal.dayKey))].sort();
            const periods = PERIODS.filter((period) => meals.some((meal) => meal.period === period));
            const weekLabel = sub.coveredWeekId
              ? `${weekRole(sub.coveredWeekId)} · ${formatSellWeek(sub.coveredWeekId)}`
              : 'Week not set';
            return (
              <section key={sub.id} className="space-y-3 rounded-xl border border-gray-200 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="font-semibold text-gray-900">{weekLabel}</h4>
                    <p className="text-sm text-gray-600">{sub.planTypeName || 'Meal plan'}</p>
                  </div>
                  {meals.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => markWeek(meals, true)}
                        className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                      >
                        Mark all this week
                      </button>
                      <button
                        type="button"
                        disabled={busy}
                        onClick={() => markWeek(meals, false)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 disabled:opacity-50"
                      >
                        Clear this week
                      </button>
                    </div>
                  )}
                </div>
                {meals.length === 0 ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-3 space-y-2">
                    <p className="text-sm text-amber-950">Meals are not on the book yet.</p>
                    <button
                      type="button"
                      disabled={busy || !sub.coveredWeekId}
                      onClick={() => onBook(sub)}
                      className="rounded-lg bg-gray-900 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50"
                    >
                      Book the meals for this week
                    </button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[36rem] border-separate border-spacing-2 text-sm">
                      <thead>
                        <tr>
                          <th className="w-28" />
                          {days.map((dayKey) => (
                            <th key={dayKey} className="px-1 text-center font-semibold text-gray-900">
                              {bookingDayLabel(dayKey)}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {periods.map((period) => (
                          <tr key={period}>
                            <th className="pr-2 text-left font-medium capitalize text-gray-700">{period}</th>
                            {days.map((dayKey) => {
                              const meal = meals.find((row) => row.dayKey === dayKey && row.period === period);
                              if (!meal) return <td key={dayKey} />;
                              if (meal.status === 'cancelled') {
                                return (
                                  <td key={dayKey} className="rounded-lg bg-gray-100 px-2 py-3 text-center text-gray-500">
                                    Cancelled
                                  </td>
                                );
                              }
                              const on = Boolean(picked[meal.id]);
                              return (
                                <td key={dayKey}>
                                  <button
                                    type="button"
                                    role="switch"
                                    aria-checked={on}
                                    disabled={busy}
                                    onClick={() => toggle(meal.id)}
                                    className={`flex w-full flex-col items-center gap-1 rounded-lg border px-2 py-3 font-semibold disabled:opacity-50 ${
                                      on
                                        ? 'border-green-700 bg-green-700 text-white'
                                        : 'border-gray-300 bg-white text-gray-800'
                                    }`}
                                  >
                                    <span className={`inline-flex h-6 w-11 items-center rounded-full p-0.5 ${on ? 'bg-green-900' : 'bg-gray-200'}`}>
                                      <span className={`h-5 w-5 rounded-full bg-white transition ${on ? 'translate-x-5' : ''}`} />
                                    </span>
                                    {on ? 'Picked up' : 'Not yet'}
                                    {on && meal.status === 'redeemed' && meal.redeemedAt && (
                                      <span className="text-xs font-normal text-green-100">{formatRecordedAt(meal.redeemedAt)}</span>
                                    )}
                                  </button>
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-4">
          <p className="text-sm text-gray-600">
            {changes.length === 0 ? 'No changes yet.' : `${changes.length} change${changes.length === 1 ? '' : 's'} to save.`}
          </p>
          <button
            type="button"
            disabled={busy || changes.length === 0}
            onClick={() => {
              const pickUpIds = changes.filter((meal) => picked[meal.id]).map((meal) => meal.id);
              const undoIds = changes.filter((meal) => !picked[meal.id]).map((meal) => meal.id);
              onSave(pickUpIds, undoIds);
            }}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          >
            Save pickups
          </button>
        </div>
      </div>
    </div>
  );
}

function eachMealPrice(plan: MealPlanType): string {
  const count = Math.max(0, plan.daysOfWeek.length * plan.periods.length);
  if (!count) return '';
  return `$${(mealPlanSalePriceDollars(plan.priceCents) / count).toFixed(2)} each`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
  const letters = parts.map((part) => part[0]?.toUpperCase() || '').join('');
  return letters || '?';
}

function PlanChoice({
  plans,
  selectedIds,
  onToggle,
  canSell,
  onSell,
}: {
  plans: MealPlanType[];
  selectedIds: string[];
  onToggle: (id: string) => void;
  canSell: boolean;
  onSell: (plans: MealPlanType[]) => void;
}) {
  const chosen = plans.filter((plan) => selectedIds.includes(plan.id));
  const totalCents = chosen.reduce((sum, plan) => sum + Math.max(0, Number(plan.priceCents) || 0), 0);
  const price = mealPlanSalePriceDollars(totalCents).toFixed(2);
  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Choose plans</p>
        <p className="text-xs text-gray-500">{plans.length} available</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {plans.map((plan) => {
          const checked = selectedIds.includes(plan.id);
          const rowPrice = mealPlanSalePriceDollars(plan.priceCents).toFixed(2);
          const count = plan.daysOfWeek.length * plan.periods.length;
          return (
            <label
              key={plan.id}
              className={`relative flex cursor-pointer flex-col rounded-2xl border-2 px-3 py-3 transition-transform duration-150 ease-out active:scale-[0.98] ${
                checked ? 'border-pink-600 bg-white' : 'border-zinc-200 bg-zinc-50'
              }`}
            >
              <input
                type="checkbox"
                className="sr-only"
                checked={checked}
                onChange={() => onToggle(plan.id)}
              />
              {checked && (
                <span className="absolute right-2 top-2 flex h-5 w-5 items-center justify-center rounded-full bg-pink-600 text-[11px] font-bold text-white" aria-hidden="true">✓</span>
              )}
              <span className="pr-6 text-sm font-semibold text-gray-900">{plan.name}</span>
              <span className="mt-2 text-2xl font-bold tabular-nums text-gray-900">${rowPrice}</span>
              <span className="mt-2 inline-flex w-fit rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-semibold text-gray-600">{count > 0 ? `${count} meal${count === 1 ? '' : 's'}` : 'Weekly'}</span>
              <span className="mt-2 text-xs text-gray-500">{planIncludes(plan) || eachMealPrice(plan)}</span>
            </label>
          );
        })}
      </div>
      <div className="mt-auto flex items-end justify-between gap-3 border-t border-zinc-100 pt-3">
        <span className="text-sm text-gray-500">{chosen.length > 1 ? `${chosen.length} plans this week` : 'Total for this week'}</span>
        <span className={`text-3xl font-bold tabular-nums ${chosen.length ? 'text-pink-600' : 'text-gray-900'}`}>${price}</span>
      </div>
      <button
        type="button"
        disabled={!canSell || chosen.length === 0}
        onClick={() => chosen.length > 0 && onSell(chosen)}
        className="w-full rounded-2xl bg-pink-600 px-3 py-3.5 text-base font-bold text-white disabled:opacity-40 transition-transform duration-150 ease-out enabled:active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
      >
        {chosen.length ? `Confirm and charge $${price}` : 'Choose a plan to charge'}
      </button>
    </div>
  );
}

function BookedWeekPanel({
  sub,
  meals,
  bookingsLoaded,
  busy,
  onPickups,
  onCancel,
  onOrderDetails,
}: {
  sub: MealPlanSubscription;
  meals: WeeklyBooking[];
  bookingsLoaded: boolean;
  busy: boolean;
  onPickups: () => void;
  onCancel: () => void;
  onOrderDetails?: () => void;
}) {
  const pickedUp = meals.filter((meal) => meal.status === 'redeemed').length;
  const total = meals.length;
  const percent = total > 0 ? Math.round((pickedUp / total) * 100) : 0;
  const weekLabel = sub.coveredWeekId
    ? `${weekRole(sub.coveredWeekId)} · ${formatSellWeek(sub.coveredWeekId)}`
    : 'Week not set';
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4 space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-emerald-800">Already booked</p>
      <div>
        <p className="text-lg font-semibold text-gray-900">{sub.planTypeName || 'Meal plan'}</p>
        <p className="text-sm text-gray-700">{weekLabel}</p>
      </div>
      <div>
        <div className="flex items-baseline justify-between gap-3 text-sm">
          <span className="font-medium text-gray-900">
            {!bookingsLoaded ? 'Checking pickups…' : total > 0 ? `Picked up ${pickedUp} of ${total}` : 'Meals not on the book yet'}
          </span>
          {total > 0 && <span className="tabular-nums text-gray-600">{percent}%</span>}
        </div>
        {total > 0 && (
          <div
            className="mt-2 h-1.5 overflow-hidden rounded-full bg-white"
            role="progressbar"
            aria-valuenow={pickedUp}
            aria-valuemin={0}
            aria-valuemax={total}
            aria-label="Meals picked up"
          >
            <div className="h-full rounded-full bg-emerald-700" style={{ width: `${percent}%` }} />
          </div>
        )}
      </div>
      <p className="text-sm text-gray-600">Booked {formatRecordedAt(sub.createdAt)} · {paymentSourceLabel(sub.paymentSource)}</p>
      <div className="flex flex-col gap-2">
        <button
          type="button"
          onClick={onPickups}
          className="rounded-xl bg-pink-600 px-3 py-2.5 text-sm font-semibold text-white transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
        >
          Mark pickups
        </button>
        {onOrderDetails && (
          <button
            type="button"
            onClick={onOrderDetails}
            className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
          >
            Order details / email receipt
          </button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={onCancel}
          className="rounded-lg border border-zinc-300 bg-white px-3 py-2 text-sm font-semibold text-gray-900 disabled:opacity-50 transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
        >
          Cancel {sub.planTypeName || 'this plan'} for {sub.coveredWeekId ? weekRole(sub.coveredWeekId) : 'this week'}
        </button>
      </div>
    </div>
  );
}

function mealPlanSalesOnOrder(order: Order): MealPlanSale[] {
  if (order.mealPlanSales?.length) return order.mealPlanSales;
  return order.mealPlanSale ? [order.mealPlanSale] : [];
}

function isUnpaidMealPlanOrder(order: Order): boolean {
  if (order.paymentStatus === 'paid') return false;
  if (order.status === 'cancelled' || order.status === 'refunded') return false;
  return mealPlanSalesOnOrder(order).length > 0;
}

const PosMealPlanOverlay: React.FC<PosMealPlanOverlayProps> = ({
  onClose,
  customers,
  menuItems,
  categories,
  orders,
  mealPlansEnabled,
  currentStaffId,
  currentStaffName,
  isManager,
  onSell,
  onOpenSaleRefund,
  onResumePayment,
  onOpenSaleDetails,
  refreshToken = 0,
}) => {
  const [showAdmin, setShowAdmin] = useState(false);
  const [extraCustomers, setExtraCustomers] = useState<Customer[]>([]);
  const [query, setQuery] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [plans, setPlans] = useState<MealPlanType[]>([]);
  const [subs, setSubs] = useState<MealPlanSubscription[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rosterBookings, setRosterBookings] = useState<WeeklyBooking[]>([]);
  const [bookingsLoaded, setBookingsLoaded] = useState(false);
  const [pickupVersion, setPickupVersion] = useState(0);
  const [sellableWeeks, setSellableWeeks] = useState<MealPlanWeek[]>([]);
  const [selectedWeekId, setSelectedWeekId] = useState(nextWeekId);
  const [planChoiceIds, setPlanChoiceIds] = useState<string[]>([]);
  const [rosterQuery, setRosterQuery] = useState('');
  const [rosterTab, setRosterTab] = useState<'pickups' | 'cancellations'>('pickups');
  const [weeksLoaded, setWeeksLoaded] = useState(false);
  const [pickupCustomerId, setPickupCustomerId] = useState('');
  const [localRefresh, setLocalRefresh] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const weekId = selectedWeekId;
  const weekReady = !weeksLoaded ? null : sellableWeeks.some((week) => week.id === weekId);

  const reloadMealPlanData = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    try {
      const [typeRows, weekRows, subRows] = await Promise.all([
        mealPlanTypesService.getAll(),
        mealPlanWeeksService.getAll(),
        mealPlanSubscriptionsService.getAll(),
      ]);
      const currentWeek = weekIdContaining();
      const upcoming = weekRows
        .filter((w) => (w.status === 'published' || w.status === 'locked') && w.id >= currentWeek)
        .sort((a, b) => a.id.localeCompare(b.id));
      setPlans(typeRows.filter((t) => t.active));
      setSellableWeeks(upcoming);
      setSelectedWeekId((prev) =>
        upcoming.some((w) => w.id === prev)
          ? prev
          : upcoming.find((w) => w.id === nextWeekId())?.id || upcoming[0]?.id || nextWeekId(),
      );
      setWeeksLoaded(true);
      setSubs(subRows);
      setPlanChoiceIds((prev) => {
        const activeIds = new Set(typeRows.filter((plan) => plan.active).map((plan) => plan.id));
        return prev.filter((id) => activeIds.has(id));
      });
      const weekIds = [...new Set(subRows.map((s) => s.coveredWeekId).filter((id): id is string => Boolean(id)))];
      if (weekIds.length === 0) {
        setRosterBookings([]);
        setBookingsLoaded(true);
      } else {
        setBookingsLoaded(false);
        const groups = await Promise.all(weekIds.map((id) => weeklyBookingsService.getByWeekId(id)));
        setRosterBookings(groups.flat());
        setBookingsLoaded(true);
      }
      setPickupVersion((n) => n + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load meal plans.');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void reloadMealPlanData();
  }, [refreshToken, localRefresh, reloadMealPlanData]);

  const directory = useMemo(() => {
    const byId = new Map<string, Customer>();
    for (const customer of [...customers, ...extraCustomers]) byId.set(String(customer.id), customer);
    return [...byId.values()];
  }, [customers, extraCustomers]);

  const filteredCustomers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q.length < 2) return [];
    return directory
      .filter((c) => `${c.name} ${c.email || ''} ${c.phone || ''}`.toLowerCase().includes(q))
      .slice(0, 6);
  }, [directory, query]);

  const customer = directory.find((c) => String(c.id) === customerId) || null;
  const weekSubs = customer
    ? subs.filter(
        (s) => String(s.customerId) === String(customer.id) && subscriptionCoversWeek(s, weekId),
      )
    : [];
  const heldPlanIds = new Set(weekSubs.map((sub) => sub.planTypeId));
  const selectedWeek = sellableWeeks.find((week) => week.id === weekId);
  const availablePlans = plans.filter(
    (plan) => !heldPlanIds.has(plan.id) && planFitsWeek(plan.periods, selectedWeek?.periods),
  );
  const already = weekSubs.length > 0;

  const unpaidMealOrders = useMemo(
    () =>
      orders
        .filter(isUnpaidMealPlanOrder)
        .sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0)),
    [orders],
  );
  const customerUnpaidOrders = customer
    ? unpaidMealOrders.filter((order) => String(order.customerId) === String(customer.id))
    : [];

  const findSaleOrder = (sub: MealPlanSubscription): Order | null => {
    if (sub.posOrderId) {
      const byId = orders.find((order) => order.id === sub.posOrderId);
      if (byId) return byId;
    }
    return (
      orders.find((order) => {
        if (order.paymentStatus !== 'paid' || order.status === 'refunded') return false;
        return mealPlanSalesOnOrder(order).some(
          (sale) =>
            String(sale.customerId) === String(sub.customerId) &&
            sale.weekId === (sub.coveredWeekId || weekId) &&
            sale.planTypeId === sub.planTypeId,
        );
      }) || null
    );
  };

  const mealsForSub = (sub: MealPlanSubscription) =>
    rosterBookings
      .filter((b) => {
        if (!sub.coveredWeekId || b.weekId !== sub.coveredWeekId) return false;
        if (String(b.customerId) !== String(sub.customerId)) return false;
        return !b.subscriptionId || b.subscriptionId === sub.id;
      })
      .sort((a, b) => a.dayKey.localeCompare(b.dayKey) || a.period.localeCompare(b.period));

  const nameForSub = (sub: MealPlanSubscription) => {
    const match = directory.find((c) => String(c.id) === String(sub.customerId));
    return match?.name || sub.customerEmail || 'Unknown customer';
  };

  const activeSubs = useMemo(
    () =>
      subs
        .filter((s) => s.status === 'active')
        .sort((a, b) => (a.coveredWeekId || '').localeCompare(b.coveredWeekId || '') || nameForSub(a).localeCompare(nameForSub(b))),
    [subs, directory],
  );

  const cancelledSubs = useMemo(
    () =>
      subs
        .filter((s) => s.status === 'cancelled')
        .sort((a, b) => (b.cancelledAt || b.updatedAt || 0) - (a.cancelledAt || a.updatedAt || 0)),
    [subs],
  );

  const bookMeals = async (sub: MealPlanSubscription) => {
    const plan = plans.find((row) => row.id === sub.planTypeId);
    const personName = nameForSub(sub);
    if (!plan || !sub.coveredWeekId || busy) {
      setError('This subscription has no plan or week, so the meals cannot be booked.');
      return;
    }
    const ok = window.confirm(
      `Book ${plan.name} for ${personName}?\n\n${weekRole(sub.coveredWeekId)} · ${formatSellWeek(sub.coveredWeekId)}\n\nThe meals will show in this list so you can switch them on.`,
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const created = await autoBookSubscriptionForWeek(sub, plan, sub.coveredWeekId);
      setPickupVersion((n) => n + 1);
      window.alert(
        created > 0
          ? `${created} meal${created === 1 ? '' : 's'} booked. Switch on the ones they collect, then save.`
          : 'Those meals were already booked.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not book the meals.');
    } finally {
      setBusy(false);
    }
  };

  const savePickups = async (pickUpIds: string[], undoIds: string[]) => {
    if (busy || (pickUpIds.length === 0 && undoIds.length === 0)) return;
    setBusy(true);
    setError(null);
    try {
      await applyMealPlanPickupToggles({
        pickUpIds,
        undoIds,
        staffId: currentStaffId,
      });
      setPickupVersion((n) => n + 1);
      setPickupCustomerId('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the pickups.');
    } finally {
      setBusy(false);
    }
  };

  const sell = async (chosen: MealPlanType[]) => {
    const selling = chosen.filter((plan) => availablePlans.some((row) => row.id === plan.id));
    if (!customer || !weekReady || busy || selling.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      await onSell(customer, selling, weekId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not start the sale.');
      setBusy(false);
    }
  };

  const cancelPlan = async (activeSub: MealPlanSubscription) => {
    if (!customer || busy) return;
    const before = isBeforeCancellationCutoff(activeSub.coveredWeekId || weekId);
    const ok = window.confirm(
      before
        ? `Cancel ${customer.name}'s meal plan?\n\nThe refund screen will open so you can refund the payment. The plan stays until that refund is recorded.`
        : `Saturday cutoff has passed for ${customer.name}.\n\nThis stops the plan. There is no refund, and this week’s meals stay.`,
    );
    if (!ok) return;

    setBusy(true);
    setError(null);
    try {
      if (!before) {
        await cancelMealPlanLocally(activeSub, false);
        setSubs(await mealPlanSubscriptionsService.getAll());
        window.alert(`${customer.name}'s plan is stopped. No refund after Saturday.`);
        return;
      }

      const fromSubscriptionId = activeSub.id.startsWith('mps-pos-') ? activeSub.id.slice('mps-pos-'.length) : '';
      const orderId =
        activeSub.posOrderId ||
        (activeSub.planTypeId && fromSubscriptionId.endsWith(`-${activeSub.planTypeId}`)
          ? fromSubscriptionId.slice(0, -(activeSub.planTypeId.length + 1))
          : fromSubscriptionId);
      let order: Order | null = orderId ? orders.find((o) => o.id === orderId) || null : null;
      if (!order && orderId) {
        order = await ordersService.getById(orderId, { preferServer: true });
      }
      if (!order) {
        order =
          orders.find((o) => {
            const sales = o.mealPlanSales?.length ? o.mealPlanSales : o.mealPlanSale ? [o.mealPlanSale] : [];
            return sales.some(
              (sale) =>
                String(sale.customerId) === String(activeSub.customerId) &&
                sale.weekId === (activeSub.coveredWeekId || weekId) &&
                (!activeSub.planTypeId || sale.planTypeId === activeSub.planTypeId),
            );
          }) || null;
      }

      const paidOnTill = Boolean(
        order &&
          order.status !== 'refunded' &&
          order.paymentStatus === 'paid' &&
          (order.payments || []).some((p) => p.amount > 0),
      );
      if (paidOnTill && order) {
        onOpenSaleRefund(order, activeSub);
        return;
      }

      await cancelMealPlanLocally(activeSub, true);
      setSubs(await mealPlanSubscriptionsService.getAll());
      window.alert(
        order?.status === 'refunded'
          ? `${customer.name}'s plan is cancelled. The sale was already refunded.`
          : `${customer.name}'s plan is cancelled. There was no till payment to refund.`,
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not cancel the meal plan.');
    } finally {
      setBusy(false);
    }
  };

  const matchesRoster = (sub: MealPlanSubscription) => {
    const q = rosterQuery.trim().toLowerCase();
    if (!q) return true;
    return `${nameForSub(sub)} ${sub.planTypeName || ''}`.toLowerCase().includes(q);
  };
  const shownActive = activeSubs.filter(matchesRoster);
  const shownCancelled = cancelledSubs.filter(matchesRoster);

  if (showAdmin) {
    return (
      <div className="fixed inset-0 z-[80] flex flex-col bg-gray-50">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 bg-white shrink-0">
          <h2 className="text-lg font-bold text-gray-900">Menu & plans</h2>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={refreshing}
              onClick={() => setLocalRefresh((n) => n + 1)}
              className="px-3 py-2 text-sm font-semibold rounded-md border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowAdmin(false);
                setLocalRefresh((n) => n + 1);
              }}
              className="px-4 py-2 text-sm font-bold rounded-md bg-gray-100 text-gray-800"
            >
              Back to sell
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-auto p-4">
          <MealPlansTab
            customers={directory}
            menuItems={menuItems}
            categories={categories}
            currentStaffId={currentStaffId}
            currentStaffName={currentStaffName}
            isManager={isManager}
            allowManualGrant={false}
            onCustomerCreated={(customer) => {
              setExtraCustomers((prev) => [...prev.filter((c) => c.id !== customer.id), customer]);
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#f6f7f9]">
      <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-zinc-200 bg-white shrink-0">
        <div className="min-w-0">
          <h2 className="text-lg font-bold text-gray-900">Meal plan</h2>
          <p className="text-sm text-gray-500 truncate">
            Selling {weekRole(weekId).toLowerCase()}
            <span className="mx-2 text-zinc-300">·</span>
            {formatSellWeek(weekId)}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            disabled={refreshing}
            onClick={() => setLocalRefresh((n) => n + 1)}
            className="px-3 py-2 text-sm font-semibold rounded-xl border border-zinc-200 text-gray-700 hover:bg-zinc-50 disabled:opacity-50"
          >
            {refreshing ? 'Refreshing…' : 'Refresh'}
          </button>
          {isManager && (
            <button
              type="button"
              onClick={() => setShowAdmin(true)}
              className="px-3 py-2 text-sm font-semibold rounded-xl border border-zinc-200 text-gray-700 hover:bg-zinc-50"
            >
              Edit menu
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-bold rounded-xl bg-gray-900 text-white"
          >
            Close
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden p-3">
        <div className="h-full min-h-0 w-full grid gap-3 lg:grid-cols-12">
          <section className="lg:col-span-7 min-h-0 h-full flex flex-col gap-3 overflow-hidden bg-white rounded-3xl border border-zinc-200 p-4">
            <div className="flex items-center justify-between gap-3 shrink-0">
              <h3 className="font-semibold text-gray-900">Sell a week</h3>
              <MealPlanNewCustomerForm
                onCreated={(created) => {
                  setExtraCustomers((prev) => [...prev.filter((c) => c.id !== created.id), created]);
                  setCustomerId(String(created.id));
                  setQuery('');
                }}
              />
            </div>
            {!weeksLoaded && (
              <p className="text-sm text-gray-500 shrink-0">Loading published weeks…</p>
            )}
            {weeksLoaded && sellableWeeks.length === 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shrink-0">
                No week is published yet. A manager needs to publish a menu under Edit menu before anyone can buy.
              </div>
            )}
            <div className="flex gap-2 overflow-x-auto pb-1 shrink-0">
              {sellableWeeks.map((week) => {
                const selected = week.id === weekId;
                const bookedCount = activeSubs.filter((sub) => sub.coveredWeekId === week.id).length;
                return (
                  <button
                    key={week.id}
                    type="button"
                    aria-pressed={selected}
                    onClick={() => setSelectedWeekId(week.id)}
                    className={`shrink-0 rounded-full px-4 py-2 text-sm font-semibold transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 ${
                      selected ? 'bg-pink-600 text-white' : 'border border-zinc-200 bg-zinc-50 text-gray-800'
                    }`}
                  >
                    {weekRole(week.id)}
                    <span className={selected ? 'text-pink-100' : 'text-gray-500'}> · {weekPillDates(week.id)}</span>
                    {week.status === 'locked' ? ' · Locked' : ''}
                    {bookedCount > 0 ? ` · ${bookedCount}` : ''}
                  </button>
                );
              })}
            </div>

            {customer ? (
              <div className="flex items-center gap-3 rounded-2xl border border-pink-100 bg-pink-50 px-3 py-3 shrink-0">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pink-600 text-sm font-semibold text-white">{initials(customer.name)}</span>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 truncate">{customer.name}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {customer.phone || 'No phone'}
                    {customer.email ? ` · ${customer.email}` : ''}
                  </p>
                  <p className="text-sm text-gray-700 truncate">
                    {already
                      ? `On ${weekSubs.map((sub) => sub.planTypeName || 'a plan').join(', ')} for ${weekRole(weekId).toLowerCase()}.`
                      : `Not booked for ${weekRole(weekId).toLowerCase()}.`}
                  </p>
                </div>
                <button
                  type="button"
                  className="shrink-0 text-sm font-bold tracking-wide text-pink-600"
                  onClick={() => {
                    setCustomerId('');
                    setQuery('');
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <>
                <input
                  autoFocus
                  className="w-full border border-zinc-200 rounded-full px-4 py-3 text-base focus:border-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-600/20"
                  placeholder="Type a name or phone"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                {query.trim().length < 2 ? (
                  <p className="text-sm text-gray-500">Type at least 2 letters. New people use Add customer.</p>
                ) : filteredCustomers.length === 0 ? (
                  <p className="text-sm text-gray-500">No match. Add them as a new customer.</p>
                ) : (
                  <div className="divide-y border border-zinc-200 rounded-xl overflow-y-auto max-h-48">
                    {filteredCustomers.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        className="block w-full text-left px-4 py-3 hover:bg-zinc-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-gray-900"
                        onClick={() => {
                          setCustomerId(String(c.id));
                          setQuery('');
                        }}
                      >
                        <div className="font-medium text-gray-900">{c.name}</div>
                        <div className="text-sm text-gray-500">
                          {c.phone || 'No phone'}
                          {c.email ? ` · ${c.email}` : ''}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
            {!mealPlansEnabled && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                Meal plans are off. Turn them on in Settings and save.
              </div>
            )}
            {weekReady === false && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                This week is not published, so it cannot be sold.
              </div>
            )}
            {error && (
              <div className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
            )}

            {plans.length === 0 && weekReady === true && (
              <p className="text-sm text-gray-500">No plans are for sale yet. Add one under Edit menu.</p>
            )}
            {plans.length > 0 && availablePlans.length === 0 && weekReady === true && (
              <p className="text-sm text-gray-500">
                No plan matches the meals on for this week. Under Edit menu, set each plan to Lunch, Dinner, or both, and turn off any plan that still includes Breakfast.
              </p>
            )}

            <div className="min-h-0 flex-1 flex flex-col gap-3 overflow-y-auto">
            {customerUnpaidOrders.length > 0 && (
              <div className="rounded-2xl border border-amber-300 bg-amber-50 p-4 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-amber-900">Payment not finished</p>
                <p className="text-sm text-amber-950">
                  Closing the payment screen leaves this ticket unpaid. The plan is not booked until payment is completed.
                </p>
                {customerUnpaidOrders.map((order) => {
                  const sales = mealPlanSalesOnOrder(order);
                  const label = sales.map((sale) => sale.planTypeName || 'Meal plan').join(', ');
                  return (
                    <div key={order.id} className="rounded-xl border border-amber-200 bg-white px-3 py-3 space-y-2">
                      <p className="font-semibold text-gray-900">{label}</p>
                      <p className="text-sm text-gray-600">
                        Order {order.id} · ${Number(order.totalAmount || 0).toFixed(2)} unpaid
                      </p>
                      <button
                        type="button"
                        onClick={() => onResumePayment(order)}
                        className="w-full rounded-xl bg-pink-600 px-3 py-2.5 text-sm font-semibold text-white transition-transform duration-150 ease-out active:scale-[0.98]"
                      >
                        Complete payment
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {!customer && unpaidMealOrders.length > 0 && (
              <div className="rounded-2xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
                {unpaidMealOrders.length} unpaid meal plan ticket{unpaidMealOrders.length === 1 ? '' : 's'} waiting for payment.
                Search the customer to complete payment, or open the order from the till takeaway list.
              </div>
            )}
            {weekSubs.map((sub) => {
              const saleOrder = findSaleOrder(sub);
              return (
              <BookedWeekPanel
                key={sub.id}
                sub={sub}
                meals={mealsForSub(sub)}
                bookingsLoaded={bookingsLoaded}
                busy={busy}
                onPickups={() => setPickupCustomerId(String(sub.customerId))}
                onCancel={() => void cancelPlan(sub)}
                onOrderDetails={saleOrder ? () => onOpenSaleDetails(saleOrder) : undefined}
              />
              );
            })}
            {availablePlans.length > 0 && (
            <PlanChoice
              plans={availablePlans}
              selectedIds={planChoiceIds.filter((id) => availablePlans.some((plan) => plan.id === id))}
              onToggle={(id) => setPlanChoiceIds((prev) => (prev.includes(id) ? prev.filter((row) => row !== id) : [...prev, id]))}
              canSell={Boolean(customer) && mealPlansEnabled && weekReady === true && !busy}
              onSell={(chosen) => void sell(chosen)}
            />
            )}
            </div>
          </section>

          <section className="lg:col-span-5 min-h-0 h-full flex flex-col bg-white rounded-3xl border border-zinc-200 p-4 gap-3 overflow-hidden">
            <div className="shrink-0 flex items-center gap-2">
              <button
                type="button"
                aria-pressed={rosterTab === 'pickups'}
                onClick={() => setRosterTab('pickups')}
                className={`rounded-full px-3 py-2 text-sm font-semibold transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 ${rosterTab === 'pickups' ? 'bg-pink-600 text-white' : 'text-gray-600 hover:bg-pink-50'}`}
              >
                Active pickups ({activeSubs.length})
              </button>
              <button
                type="button"
                aria-pressed={rosterTab === 'cancellations'}
                onClick={() => setRosterTab('cancellations')}
                className={`rounded-full px-3 py-2 text-sm font-semibold transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600 ${rosterTab === 'cancellations' ? 'bg-pink-600 text-white' : 'text-gray-600 hover:bg-pink-50'}`}
              >
                Cancellations ({cancelledSubs.length})
              </button>
            </div>
            <h3 className="sr-only">{rosterTab === 'pickups' ? 'Active pickups' : 'Cancellations'}</h3>
            <input
              className="shrink-0 w-full rounded-full border border-zinc-200 px-4 py-2 text-sm focus:border-pink-600 focus:outline-none focus:ring-2 focus:ring-pink-600/20"
              placeholder="Filter by name or plan"
              value={rosterQuery}
              onChange={(event) => setRosterQuery(event.target.value)}
            />
            {rosterTab === 'pickups' && shownActive.length === 0 && (
              <p className="text-sm text-gray-500">{activeSubs.length === 0 ? 'Nobody is subscribed right now.' : 'No one matches that filter.'}</p>
            )}
            {rosterTab === 'pickups' && (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
              {shownActive.map((sub) => {
                const meals = mealsForSub(sub);
                const pickedUp = meals.filter((b) => b.status === 'redeemed').length;
                const total = meals.length;
                const personName = nameForSub(sub);
                return (
                  <div key={sub.id} className="rounded-2xl border border-zinc-200 px-3 py-3 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <button
                        type="button"
                        className="min-w-0 flex items-start gap-3 text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gray-900"
                        onClick={() => {
                          setCustomerId(String(sub.customerId));
                          setQuery('');
                          if (sub.coveredWeekId && sellableWeeks.some((week) => week.id === sub.coveredWeekId)) {
                            setSelectedWeekId(sub.coveredWeekId);
                          }
                        }}
                      >
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-zinc-100 text-xs font-semibold text-gray-800">{initials(personName)}</span>
                        <span className="min-w-0">
                          <span className="flex items-center gap-2">
                            <span className="font-semibold text-gray-900 truncate">{personName}</span>
                            <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">Active</span>
                          </span>
                          <span className="block text-sm text-gray-600 truncate">{sub.planTypeName || 'Meal plan'}</span>
                        </span>
                      </button>
                      <p className="shrink-0 text-sm font-semibold tabular-nums text-gray-900">
                        {!bookingsLoaded ? '…' : total > 0 ? `${pickedUp} / ${total} picked` : '—'}
                      </p>
                    </div>
                    {total > 0 && (
                      <div
                        className="h-1.5 overflow-hidden rounded-full bg-zinc-100"
                        role="progressbar"
                        aria-valuenow={pickedUp}
                        aria-valuemin={0}
                        aria-valuemax={total}
                        aria-label={`Picked up ${pickedUp} of ${total}`}
                      >
                        <div className="h-full rounded-full bg-pink-500" style={{ width: `${Math.round((pickedUp / total) * 100)}%` }} />
                      </div>
                    )}
                    <p className="text-xs text-gray-500">
                      {sub.coveredWeekId ? `${weekRole(sub.coveredWeekId)} · ${formatSellWeek(sub.coveredWeekId)}` : 'Week not set'}
                      {' · '}Booked {formatRecordedAt(sub.createdAt)}
                      {' · '}{paymentSourceLabel(sub.paymentSource)}
                    </p>
                    {!bookingsLoaded ? null : total === 0 ? (
                      <p className="text-xs text-amber-800">Meals not on the book yet</p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() => setPickupCustomerId(String(sub.customerId))}
                      className="w-full rounded-xl bg-pink-600 px-3 py-2.5 text-sm font-semibold text-white transition-transform duration-150 ease-out active:scale-[0.98] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-pink-600"
                    >
                      Mark pickup
                    </button>
                  </div>
                );
              })}
            </div>
            )}
            {rosterTab === 'cancellations' && shownCancelled.length === 0 && (
              <p className="text-sm text-gray-500">{cancelledSubs.length === 0 ? 'No cancellations yet.' : 'No one matches that filter.'}</p>
            )}
            {rosterTab === 'cancellations' && (
            <div className="flex-1 min-h-0 overflow-y-auto space-y-2">
              {shownCancelled.map((sub) => (
                <div key={sub.id} className="rounded-2xl border border-rose-100 bg-rose-50/60 px-3 py-3 space-y-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-semibold text-gray-900">{nameForSub(sub)}</p>
                    <span className="text-[11px] font-bold tracking-wide text-rose-700">CANCELLED</span>
                  </div>
                  <p className="text-sm text-gray-700">{sub.planTypeName || 'Meal plan'}</p>
                  <p className="text-xs text-gray-500">
                    {sub.coveredWeekId ? `${weekRole(sub.coveredWeekId)} · ${formatSellWeek(sub.coveredWeekId)}` : 'Week not set'}
                  </p>
                  <p className="text-xs text-gray-500">Booked {formatRecordedAt(sub.createdAt)} · Cancelled {formatRecordedAt(sub.cancelledAt || sub.updatedAt)}</p>
                  <p className="text-xs text-gray-500">{paymentSourceLabel(sub.paymentSource)}</p>
                </div>
              ))}
            </div>
            )}
          </section>
        </div>
      </div>
      {pickupCustomerId && (
        <MealPickupModal
          customerName={
            directory.find((c) => String(c.id) === pickupCustomerId)?.name
            || activeSubs.find((sub) => String(sub.customerId) === pickupCustomerId)?.customerEmail
            || 'Customer'
          }
          weeks={activeSubs
            .filter((sub) => String(sub.customerId) === pickupCustomerId)
            .map((sub) => ({ sub, meals: mealsForSub(sub) }))}
          bookingsLoaded={bookingsLoaded}
          busy={busy}
          onClose={() => setPickupCustomerId('')}
          onBook={(sub) => void bookMeals(sub)}
          onSave={(pickUpIds, undoIds) => void savePickups(pickUpIds, undoIds)}
        />
      )}
    </div>
  );
};

export default PosMealPlanOverlay;
